import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from 'framer-motion';
import React from 'react';
import { useI18n } from '../../i18n/I18nProvider';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const FAQ: React.FC = () => {
  const { t } = useI18n();

  const faqItems: FAQItem[] = [
    {
      question: t.faq.audience.question,
      answer: t.faq.audience.answer,
    },
    {
      question: t.faq.prerequisites.question,
      answer: t.faq.prerequisites.answer,
    },
    {
      question: t.faq.tools.question,
      answer: t.faq.tools.answer,
    },
    {
      question: t.faq.integration.question,
      answer: t.faq.integration.answer,
    },
    {
      question: t.faq.learn.question,
      answer: (
        <ul className="list-disc pl-6 space-y-2">
          <li>{t.faq.learn.items.setup}</li>
          <li>{t.faq.learn.items.components}</li>
          <li>{t.faq.learn.items.interactions}</li>
          <li>{t.faq.learn.items.dependencies}</li>
          <li>{t.faq.learn.items.maintainable}</li>
          <li>{t.faq.learn.items.bestPractices}</li>
        </ul>
      ),
    },
    {
      question: t.faq.examples.question,
      answer: t.faq.examples.answer,
    },
    {
      question: t.faq.updates.question,
      answer: t.faq.updates.answer,
    },
    {
      question: t.faq.format.question,
      answer: t.faq.format.answer,
    },
    {
      question: t.faq.quality.question,
      answer: t.faq.quality.answer,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="py-16 relative overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50"
        animate={{
          background: [
            'linear-gradient(to bottom right, #f9fafb, #f0f9ff, #faf5ff)',
            'linear-gradient(to bottom right, #f9fafb, #e0f2fe, #f5f3ff)',
            'linear-gradient(to bottom right, #f9fafb, #dbeafe, #ede9fe)',
            'linear-gradient(to bottom right, #f9fafb, #f0f9ff, #faf5ff)',
          ],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <div className="container mx-auto px-4 relative z-10">
        <motion.h2 
          className="text-3xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {t.faq.title}
        </motion.h2>
        <motion.div 
          className="max-w-3xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="border-b border-gray-200"
                >
                  <AccordionTrigger className="text-xl font-semibold text-gray-800 hover:no-underline group">
                    <motion.span
                      className="inline-block"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.question}
                    </motion.span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {item.answer}
                    </motion.div>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ; 